import { HttpErrorResponse } from '@angular/common/http'
import { inject, ErrorHandler, Injectable } from '@angular/core'
import { ToastService } from '@/app/shared/services/toast.service'
import { trackEvent } from '@aptabase/web'

function isHttpError(e: unknown): e is HttpErrorResponse {
  return !!e && typeof e === 'object' && 'status' in e && 'message' in e
}

// Échec de chargement d'un CHUNK lazy : typiquement un onglet resté ouvert sur une version obsolète
// après un déploiement — l'index en mémoire référence un hash de chunk qui n'existe plus sur le serveur
// (→ 404, ou index.html renvoyé = « module script MIME text/html »).
const CHUNK_ERROR_RE =
  /ChunkLoadError|dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch dynamically imported/i

function isChunkLoadError(e: unknown): boolean {
  const err = e as { name?: string; message?: string } | undefined
  return err?.name === 'ChunkLoadError' || CHUNK_ERROR_RE.test(err?.message ?? String(e ?? ''))
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly toast = inject(ToastService)

  handleError(error: unknown): void {
    // Les erreurs HTTP sont déjà gérées (toast métier) par HttpErrorInterceptor : ici on ne traite que
    // les erreurs JS NON capturées (bug applicatif).
    if (isHttpError(error)) {
      console.error('[HTTP Error]', error.message)
      return
    }

    // Chunk lazy introuvable (déploiement pendant que l'onglet était ouvert) : on recharge UNE fois
    // pour récupérer l'index + les chunks à jour. Garde sessionStorage = pas de boucle si l'échec
    // persiste (vrai problème réseau/serveur) ; le drapeau est levé au prochain bootstrap réussi (main.ts).
    if (isChunkLoadError(error)) {
      const KEY = 'gaco.chunkReloaded'
      if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, '1')
        window.location.reload()
        return
      }
      this.toast.show('error', 'Une nouvelle version est disponible. Rechargez la page (Ctrl+Maj+R).')
      return
    }

    this.toast.show('error', 'Une erreur est survenue.')

    // Télémétrie (Aptabase, déjà initialisé dans main.ts) : on remonte le message + une stack tronquée
    // pour que les bugs de prod ne disparaissent plus en silence. Sans PII (message technique + code).
    const err = error as { message?: string; stack?: string } | undefined
    trackEvent('client_error', {
      message: (err?.message ?? String(error)).slice(0, 300),
      stack: (err?.stack ?? '').slice(0, 500),
    })

    if (typeof window !== 'undefined' && (window as any).ngDevMode) {
      console.error('[Global Error Stack]', error)
    }
  }
}

